// Formatting + colour rules for the home dashboard. The colour rules are the backend's
// (docs/dashboard/Home-Dashboard-API.md "SiteHealth"), repeated here only to pick a colour.

export const fmt = n =>
    n === null || n === undefined || Number.isNaN(Number(n))
        ? '0'
        : Number(n).toLocaleString()

const UNIT_SINGULAR = {
    audits: 'audit',
    inspections: 'inspection',
    checklists: 'checklist',
    records: 'record',
}

// unit(1) = "audit", unit(2) = "audits"; Unit() = "Audits"
export const makeUnit = unitWord => {
    const plural = UNIT_SINGULAR[String(unitWord || '').toLowerCase()]
        ? String(unitWord).toLowerCase()
        : 'audits'
    const unit = n => (Number(n) === 1 ? UNIT_SINGULAR[plural] : plural)
    const Unit = n => {
        const w = unit(n === undefined ? 2 : n)
        return w.charAt(0).toUpperCase() + w.slice(1)
    }
    return { unit, Unit }
}

// Dates without a zone are read as browser-local (stored server times); values the API marks as UTC
// (ticket / failed-email activity) end with "Z" and are converted by the browser.
export const toDate = value => {
    if (!value) return null
    const d = new Date(value)
    return Number.isNaN(d.getTime()) ? null : d
}

export const formatTime = value => {
    const d = toDate(value)
    return d ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''
}

export const formatDay = value => {
    const d = toDate(value)
    if (!d) return ''
    const today = new Date()
    const startOf = x => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime()
    const diff = Math.round((startOf(today) - startOf(d)) / 86400000)
    if (diff === 0) return `today ${formatTime(d)}`
    if (diff === 1) return 'yesterday'
    return d.toLocaleDateString([], { day: 'numeric', month: 'short' })
}

export const formatLongDate = (value = new Date()) =>
    toDate(value)?.toLocaleDateString([], {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
    }) || ''

export const greeting = (date = new Date()) => {
    const h = date.getHours()
    if (h < 12) return 'Good morning'
    if (h < 18) return 'Good afternoon'
    return 'Good evening'
}

export const firstName = user => {
    const full = String(user?.FullName || user?.Name || user?.UserName || '').trim()
    if (!full || full.includes('@')) return ''
    return full.split(/\s+/)[0]
}

export const scoreClass = s => {
    if (s === null || s === undefined) return ''
    if (s >= 85) return 'ok'
    if (s >= 75) return 'warn'
    return 'bad'
}

export const findingsClass = n => {
    if (!n) return 'ok'
    if (n >= 3) return 'bad'
    return 'warn'
}

const HEAT = {
    bad: 'var(--hd-heat-bad)',
    warn: 'var(--hd-heat-warn)',
    ok: 'var(--hd-heat-ok)',
    great: 'var(--hd-heat-great)',
}

export const heatColorForScore = score => {
    if (score === null || score === undefined) return 'var(--hd-mut)'
    if (score < 75) return HEAT.bad
    if (score < 85) return HEAT.warn
    if (score < 90) return HEAT.ok
    return HEAT.great
}

// count mode: red = escalated or 3+ open, amber = 1-2 open, green = 0 open with findings, dark green = none
// rate mode: red >= 75 per 100 or escalated, amber 40-74, green below 40, dark green none open
export const heatColorForItem = (item, mode) => {
    const open = Number(item?.Open || 0)
    const esc = Number(item?.Escalated || 0)
    if (mode === 'rate') {
        const rate = Number(item?.RatePer100 || 0)
        if (esc > 0 || rate >= 75) return HEAT.bad
        if (open === 0) return HEAT.great
        if (rate >= 40) return HEAT.warn
        return HEAT.ok
    }
    if (esc > 0 || open >= 3) return HEAT.bad
    if (open >= 1) return HEAT.warn
    if (Number(item?.Findings || 0) > 0) return HEAT.ok
    return HEAT.great
}

export const kindClass = kind => {
    const k = String(kind || '').toLowerCase()
    return k === 'bad' || k === 'warn' || k === 'info' ? k : 'info'
}

export const activityClass = kind => {
    switch (String(kind || '').toLowerCase()) {
        case 'completed':
            return 'ok'
        case 'escalated':
        case 'emailfailed':
            return 'bad'
        case 'ticket':
            return 'warn'
        default:
            return 'info'
    }
}

export const ACTION_LABELS = {
    open: 'Open',
    assign: 'Assign',
    view: 'View',
    reassign: 'Reassign',
    acknowledge: 'Acknowledge',
    createticket: 'Create ticket',
    resend: 'Resend',
    resume: 'Resume',
    review: 'Review',
}

export const actionLabel = action =>
    ACTION_LABELS[String(action || '').toLowerCase()] || 'Open'
